import { createContext, useContext, useEffect, useState } from 'react';
import { app, db } from './firebase';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import axios from 'axios';

const AuthContext = createContext({});
const auth = getAuth(app);

// Function to verify and sync subscription status with Stripe
const verifySubscriptionStatus = async (userId) => {
    try {
        // Call backend to check actual Stripe subscription status
        const response = await axios.get(`https://api.frazza.online/subscription-status/${userId}`);
        
        if (response.data && response.data.status) {
            const isActive = response.data.status === 'active' || response.data.status === 'trialing';
            
            // Update Firebase with current status
            const userRef = doc(db, 'UserInformation', userId);
            const userSnap = await getDoc(userRef);
            
            if (userSnap.exists()) {
                const currentData = userSnap.data();
                
                // Only update if status changed
                if (currentData.member !== isActive) {
                    await updateDoc(userRef, {
                        member: isActive,
                        subscriptionStatus: response.data.status,
                        lastSubscriptionCheck: new Date(),
                        ...(isActive && { subscriptionCanceledAt: null })
                    });
                    
                    // Also update partner if exists
                    const partnerId = currentData.partnerId || currentData.inRelationWith;
                    if (partnerId) {
                        const partnerRef = doc(db, 'UserInformation', partnerId);
                        await updateDoc(partnerRef, {
                            member: isActive,
                            subscriptionStatus: response.data.status,
                            ...(isActive && { subscriptionCanceledAt: null })
                        });
                    }
                }
            }
        }
    } catch (error) {
        console.error('Error verifying subscription:', error);
    }
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [userLoggedIn, setUserLoggedIn] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
            if (fbUser) {
                setUser(fbUser);
                setUserLoggedIn(true);
                
                // Verify subscription status on every login/page load
                verifySubscriptionStatus(fbUser.uid);
            } else {
                setUser(null);
                setUserLoggedIn(false);
            }
            setLoading(false);
        });
        return unsubscribe;
    }, []);

    return (
        <AuthContext.Provider value={{ user, userLoggedIn, loading }}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    return useContext(AuthContext);
};